import { Fragment, Suspense, useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from 'react-router-dom';

import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import UnauthenticatedLayout from '@/unauthenticatedPages/_layout';
import { useModals } from '@/router';
import { generateModalRoutes, generatePreservedRoutes, generateRegularRoutes } from './core';

import type { ActionFunction, LoaderFunction, RouteObject } from 'react-router-dom';

type Element = () => JSX.Element;
type Module = {
  default: Element;
  Action?: ActionFunction;
  Catch?: Element;
  Loader?: LoaderFunction;
  Pending?: Element;
};

const PRESERVED = import.meta.glob<Module>('/src/pages/(_app|404).{jsx,tsx}', { eager: true });
const PUBLIC = import.meta.glob<Module>('/src/public/(_app|404).{jsx,tsx}', { eager: true });
const MODALS = import.meta.glob<Pick<Module, 'default'>>('/src/pages/**/[+]*.{jsx,tsx}', { eager: true });
const ROUTES = import.meta.glob<Module>(['/src/pages/**/[\\w[-]*.{jsx,tsx}', '!**/(_app|404).*'], { eager: true });
const PUBLIC_ROUTES = import.meta.glob<Module>(['/src/public/**/[\\w[-]*.{jsx,tsx}', '!**/(_app|404).*'], {
  eager: true
});
const UNAUTHENTICATED_ROUTES = import.meta.glob<Module>(['/src/unauthenticatedPages/**/[\\w[-]*.{jsx,tsx}', '!**/(_app|404).*'], {
  eager: true
});

const preservedRoutes = generatePreservedRoutes<Omit<Module, 'Action'>>(PRESERVED);
const publicRoutes = generatePreservedRoutes<Omit<Module, 'Action'>>(PUBLIC, true);
const modalRoutes = generateModalRoutes<Element>(MODALS);

const regularRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(ROUTES, (module, key) => {
  const index = /index\.(jsx|tsx)$/.test(key) && !key.includes('pages/index') ? { index: true } : {};
  const Element = module?.default || Fragment;
  const Page = () =>
    module?.Pending ? (
      <Suspense fallback={<module.Pending />}>
        <Element />
      </Suspense>
    ) : (
      <Element />
    );

  return {
    ...index,
    Component: () => (
      <ProtectedRoute guardType="authenticated">
        <Page />
      </ProtectedRoute>
    ),
    ErrorBoundary: module?.Catch,
    action: module?.Action,
    loader: module?.Loader
  };
});

/*
  To allow code splitting and lazy loading:
  - replace the regularRoutes with the one commented out.
  - from line 28, remove the { eager: true }.
*/

// const regularRoutes = generateRegularRoutes<RouteObject, () => Promise<Partial<Module>>>(ROUTES, (module, key) => {
//   const index = /index\.(jsx|tsx)$/.ioSettings(key) && !key.includes('pages/index') ? { index: true } : {}

//   return {
//     ...index,
//     lazy: async () => {
//       const Element = (await module())?.default || Fragment
//       const Pending = (await module())?.Pending
//       const Page = () => (Pending ? <Suspense fallback={<Pending />} children={<Element />} /> : <Element />)

//       return {
//         Component: Page,
//         ErrorBoundary: (await module())?.Catch,
//         loader: (await module())?.Loader,
//         action: (await module())?.Action,
//       }
//     },
//   }
// })

const publicRegularRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(
  PUBLIC_ROUTES,
  (module, key) => {
    const index = /index\.(jsx|tsx)$/.test(key) && !key.includes('public/index') ? { index: true } : {};
    const Element = module?.default || Fragment;
    const Page = () =>
      module?.Pending ? (
        <Suspense fallback={<module.Pending />}>
          <Element />
        </Suspense>
      ) : (
        <Element />
      );

    return {
      ...index,
      Component: () => <Page />,
      ErrorBoundary: module?.Catch,
      action: module?.Action,
      loader: module?.Loader
    };
  },
  true
);

const unauthenticatedRegularRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(
  UNAUTHENTICATED_ROUTES,
  (module, key) => {
    const index = /index\.(jsx|tsx)$/.test(key) && !key.includes('unauthenticatedPages/index') ? { index: true } : {};
    const Element = module?.default || Fragment;
    const Page = () =>
      module?.Pending ? (
        <Suspense fallback={<module.Pending />}>
          <Element />
        </Suspense>
      ) : (
        <Element />
      );

    return {
      ...index,
      Component: () => (
        <ProtectedRoute guardType="unauthenticated">
          <Page />
        </ProtectedRoute>
      ),
      ErrorBoundary: module?.Catch,
      action: module?.Action,
      loader: module?.Loader
    };
  },
  false,
  true
);

const _app = preservedRoutes?._app;
const _404 = preservedRoutes?.['404'];

const _publicApp = publicRoutes?._app;
const _public404 = publicRoutes?.['404'];

const Element = _app?.default || Fragment;
const App = () =>
  _app?.Pending ? (
    <Suspense fallback={<_app.Pending />}>
      <Element />
    </Suspense>
  ) : (
    <Element />
  );

const PublicElement = _publicApp?.default || Fragment;
const PublicApp = () =>
  _publicApp?.Pending ? (
    <Suspense fallback={<_publicApp.Pending />}>
      <PublicElement />
    </Suspense>
  ) : (
    <PublicElement />
  );

const app = {
  Component: () => <ProtectedRoute guardType="authenticated">{_app?.default ? <App /> : <Outlet />}</ProtectedRoute>,
  ErrorBoundary: _app?.Catch,
  loader: _app?.Loader
};
const fallback = {
  Component: _404?.default || Fragment,
  path: '*'
};

const publicApp = {
  Component: () => (_publicApp?.default ? <PublicApp /> : <Outlet />),
  ErrorBoundary: _publicApp?.Catch,
  loader: _publicApp?.Loader
};
const publicFallback = {
  Component: _public404?.default || Fragment,
  path: '*'
};

export const routes: RouteObject[] = [
  { Component: UnauthenticatedLayout, children: [...unauthenticatedRegularRoutes] },
  { ...app, children: [...regularRoutes, fallback] },
  { ...publicApp, children: [...publicRegularRoutes, publicFallback] },
];
export const Routes = () => <RouterProvider router={createBrowserRouter(routes)} />;
export const Modals = ({ id }: { id: 'global' | string }) => {
  const location = useLocation();
  const modals = useModals();
  const { modal = '', modalId = '', modalGlobal = false } = location.state || {};
  const isTypeGlobal = id === 'global';
  const shouldRenderModal = (id === modalId && !modalGlobal) || (isTypeGlobal && modalGlobal);

  useEffect(() => {
    const handleBeforeUnload = () => modals.close();
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (shouldRenderModal) {
    const ModalComponent = modalRoutes[modal] || Fragment;
    return (
      <ProtectedRoute guardType="authenticated">
        <ModalComponent />
      </ProtectedRoute>
    );
  }

  return null;
};
